/* global setTimeout, clearTimeout */
/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Orchestrates a full widget refresh: build the data snapshot, rasterize the
 * stats sparkline (when the chart host is mounted), and publish everything to
 * OS-shared storage via the bridge. Also owns the periodic background refresh
 * registration (expo-background-fetch + expo-task-manager) so widgets stay warm
 * even while the app is backgrounded.
 *
 * Every path is guarded and idempotent — safe to call on foreground, after
 * login, from the Widgets screen's "refresh now", and from the background task.
 */

import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';

import buildWidgetSnapshot from './buildWidgetSnapshot';
import { writeWidgetSnapshot, syncWidgetAuthToken, reloadWidgetTimelines } from './widgetBridge';
import { captureWidgetChart } from './chartCaptureRegistry';
import { getAccessToken } from '../storage';
import { readAutoRefreshMinutes } from './widgetRefreshPrefs';

/** Background task identifier (expo-task-manager). */
export const WIDGET_REFRESH_TASK = 'HG_WIDGET_REFRESH_TASK';

/** Longest a snapshot build may take before the pass gives up and lets the extension pull by itself (ms). */
export const SNAPSHOT_BUILD_TIMEOUT_MS = 25000;

/** Floor of the background refresh interval (minutes): the OS never wakes the app more often than this. */
const BACKGROUND_MIN_MINUTES = 15;

/** The refresh currently executing, or `null` when idle. */
let refreshInFlight = null;
/**
 * The single coalesced follow-up chained behind `refreshInFlight`, or `null`.
 * At most ONE is ever queued: a burst of triggers collapses into one extra run.
 */
let queuedRefresh = null;

/**
 * Reject when `promise` has not settled within `ms` (the timer is always cleared).
 * @param {Promise<*>} promise The job to bound.
 * @param {number} ms Timeout in milliseconds.
 * @returns {Promise<*>}
 */
const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('widget snapshot build timed out')), ms);
    Promise.resolve(promise).then(
        (value) => {
            clearTimeout(timer);
            resolve(value);
        },
        (error) => {
            clearTimeout(timer);
            reject(error);
        }
    );
});

/**
 * One refresh pass: build the snapshot, rasterize the sparkline when the chart
 * host is mounted, then publish to OS-shared storage. Never throws — resolves
 * `null` on a logged-out user or any failure.
 * @returns {Promise<object|null>}
 */
const publishWidgetSnapshot = async () => {
    let token = null;
    try {
        token = await getAccessToken();
    } catch (_error) {
        token = null;
    }
    if (!token) {
        // Logged out — leave the last snapshot in place.
        return null;
    }

    try {
        const snapshot = await withTimeout(buildWidgetSnapshot(), SNAPSHOT_BUILD_TIMEOUT_MS);

        // Rasterize the sparkline if the in-app chart host is mounted.
        if (Array.isArray(snapshot?.stats?.series) && snapshot.stats.series.length > 0) {
            const { light, dark } = await captureWidgetChart(snapshot.stats.series);
            snapshot.stats.chartImageLight = light;
            snapshot.stats.chartImageDark = dark;
        }

        // The write below reloads the timelines itself, so the token sync must not reload twice.
        await syncWidgetAuthToken({ reload: false });
        await writeWidgetSnapshot(snapshot);

        return snapshot;
    } catch (_error) {
        // The build failed or hung (offline, slow API): the extension pulls `/widgets/summary` with the App Group token
        // on its own, so make sure the token is current and WidgetKit rebuilds the timelines instead of leaving them
        // on stale data until the next successful publish.
        try {
            await syncWidgetAuthToken({ reload: false });
        } catch (_syncError) {
            // ignore
        }
        reloadWidgetTimelines();

        return null;
    }
};

/**
 * Start a refresh pass and track it as the in-flight one. The tracker is only
 * cleared by the pass that owns it, so a later run can never null out a newer
 * one's slot.
 * @returns {Promise<object|null>}
 */
const startRefresh = () => {
    const run = publishWidgetSnapshot()
        .catch(() => null)
        .then((snapshot) => {
            if (refreshInFlight === run) {
                refreshInFlight = null;
            }

            return snapshot;
        });

    refreshInFlight = run;

    return run;
};

/**
 * Build + publish a fresh widget snapshot. No-ops (leaves the last snapshot in
 * place) when the user is logged out. Returns the published snapshot, or null.
 *
 * Concurrency: a request that arrives while a refresh is already running is NOT
 * dropped — it queues exactly one follow-up pass that starts after the current
 * one settles. Previously such a request was handed the in-flight promise, so a
 * `widget.refresh` push landing mid-refresh was answered with the PRE-change
 * snapshot and the widget kept showing stale state until the next trigger.
 * Coalesced, never stacked: N concurrent requests cost at most one extra pass.
 *
 * @returns {Promise<object|null>}
 */
export const refreshWidgets = async () => {
    if (!refreshInFlight) {
        return startRefresh();
    }

    if (!queuedRefresh) {
        queuedRefresh = refreshInFlight
            .catch(() => null)
            .then(() => {
                // Released BEFORE the follow-up starts, so anything arriving
                // during that pass can queue its own successor.
                queuedRefresh = null;

                return startRefresh();
            });
    }

    return queuedRefresh;
};

/**
 * Whether a push changes what the collaboration widgets show — a Team Chat
 * message or meeting event (TeamChat / Meetings widgets) or a VoIP call event
 * (Calls widget). The push handlers call {@link refreshWidgets} for these on
 * top of their normal banner handling, so the widgets repaint within seconds
 * instead of on the ~30-minute background fetch. Bursts are cheap: refreshes
 * coalesce onto at most one follow-up pass.
 * @param {object|undefined} data Push `data` payload.
 * @returns {boolean}
 */
export const isWidgetRelevantPush = (data) => {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const type = String(data.type || '').toLowerCase();
    const eventKey = String(data.eventKey || data.eventkey || '').toLowerCase();

    return type === 'chat'
        || type === 'chat-meeting'
        || eventKey.startsWith('chat.message')
        || eventKey.startsWith('chat.meeting')
        || eventKey.startsWith('voip.call');
};

/** The mail-only publish currently executing, or `null` when idle. */
let mailRefreshInFlight = null;

/**
 * One mail-only publish pass: resolve the fresh `mail` section (either from the
 * caller's already-fetched inbox data or via the light backend fetch), overlay
 * it — together with freshly localized `labels.mail` — onto the LAST published
 * snapshot, and republish. Every other section (timer, calendar, stats, chart
 * images…) is carried over untouched, so this is far cheaper than a full
 * {@link refreshWidgets} pass and safe to run on every inbox poll tick.
 * Never throws — resolves `null` on a logged-out user or any failure.
 *
 * @param {object} [mailData] Optional pre-fetched feed (see `normalizeMailFeed`).
 * @returns {Promise<object|null>} The republished snapshot, or `null`.
 */
const publishMailSection = async (mailData) => {
    try {
        const token = await getAccessToken();
        if (!token) {
            // Logged out — leave the last snapshot in place.
            return null;
        }

        // Lazily required so the jest mocks of `buildWidgetSnapshot` (which stub
        // only the default export), the module import graph and non-widget
        // builds stay unaffected — `widgetConfig` transitively pulls native
        // modules this module must not load eagerly.
        // eslint-disable-next-line global-require
        const { normalizeMailFeed, fetchMailSection, buildMailLabels } = require('./buildWidgetSnapshot');
        const mail = mailData ? normalizeMailFeed(mailData) : await fetchMailSection();
        if (!mail) {
            return null;
        }

        let prior = {};
        try {
            // eslint-disable-next-line global-require
            const AsyncStorage = require('@react-native-async-storage/async-storage').default;
            // eslint-disable-next-line global-require
            const { WIDGET_SNAPSHOT_KEY } = require('../../config/widgetConfig');
            const raw = await AsyncStorage.getItem(WIDGET_SNAPSHOT_KEY);
            prior = raw ? JSON.parse(raw) : {};
        } catch (_error) {
            prior = {};
        }

        const snapshot = {
            ...prior,
            generatedAt: new Date().toISOString(),
            mail,
            labels: { ...(prior.labels || {}), mail: buildMailLabels() },
        };

        await writeWidgetSnapshot(snapshot);

        return snapshot;
    } catch (_error) {
        return null;
    }
};

/**
 * Publish ONLY the mail widget data ({ unread count + latest inbox rows }) into
 * the widget snapshot. Called by the mail client after every inbox fetch, poll
 * fallback tick and push/socket receipt (see `MailContext`), so the home-screen
 * mail widget stays in step with the mailbox without paying for a full snapshot
 * rebuild. Concurrent callers are coalesced onto the in-flight pass.
 *
 * @param {{
 *   unreadCount?: number,
 *   messages?: Array<{ id: string, threadKey?: string|null, fromName?: string,
 *     subject?: string, receivedAt?: string|null, seen?: boolean }>,
 *   configured?: boolean,
 * }} [mailData] Optional already-fetched inbox feed; fetched internally when omitted.
 * @returns {Promise<object|null>} The republished snapshot, or `null`.
 */
export const refreshMailWidgetData = async (mailData) => {
    if (mailRefreshInFlight) {
        return mailRefreshInFlight;
    }

    const run = publishMailSection(mailData)
        .catch(() => null)
        .then((snapshot) => {
            if (mailRefreshInFlight === run) {
                mailRefreshInFlight = null;
            }

            return snapshot;
        });
    mailRefreshInFlight = run;

    return run;
};

// Register the background task exactly once at module load. The task body only
// refreshes data/text (the chart host is not mounted in the background, so the
// chart image simply carries over from the last foreground capture).
if (!TaskManager.isTaskDefined(WIDGET_REFRESH_TASK)) {
    TaskManager.defineTask(WIDGET_REFRESH_TASK, async () => {
        try {
            const snapshot = await refreshWidgets();

            return snapshot
                ? BackgroundFetch.BackgroundFetchResult.NewData
                : BackgroundFetch.BackgroundFetchResult.NoData;
        } catch (_error) {
            return BackgroundFetch.BackgroundFetchResult.Failed;
        }
    });
}

/**
 * Register the periodic background refresh. Best-effort — silently ignored on
 * platforms/simulators where background fetch is unavailable.
 * @returns {Promise<void>}
 */
export const registerWidgetBackgroundRefresh = async () => {
    try {
        // The user's auto-reload choice (default 15 min), floored at what the OS grants a background task.
        const minutes = Math.max(BACKGROUND_MIN_MINUTES, await readAutoRefreshMinutes());
        await BackgroundFetch.registerTaskAsync(WIDGET_REFRESH_TASK, {
            minimumInterval: minutes * 60,
            stopOnTerminate: false,
            startOnBoot: true,
        });
    } catch (_error) {
        // Background fetch unsupported / already registered — ignore.
    }
};

/**
 * Unregister the background refresh (e.g. on logout). Best-effort.
 * @returns {Promise<void>}
 */
export const unregisterWidgetBackgroundRefresh = async () => {
    try {
        await BackgroundFetch.unregisterTaskAsync(WIDGET_REFRESH_TASK);
    } catch (_error) {
        // Not registered — ignore.
    }
};

/**
 * Re-register the background refresh so a changed auto-reload interval takes effect. Best-effort.
 * @returns {Promise<void>}
 */
export const reregisterWidgetBackgroundRefresh = async () => {
    await unregisterWidgetBackgroundRefresh();
    await registerWidgetBackgroundRefresh();
};

export default refreshWidgets;
